import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Smartphone, X, MonitorDown, Share2 } from 'lucide-react';
import usePwaInstallPrompt from '../../shared/hooks/usePwaInstallPrompt';

function InstallHelpModal({ open, onClose, platform, incognito }) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`onlineDemo.pwaInstall.${key}`, opts);

  if (!open) return null;

  const steps =
    platform === 'ios'
      ? [p('step_ios_1'), p('step_ios_2'), p('step_ios_3')]
      : platform === 'edge'
        ? [
            incognito ? p('step_edge_incognito') : null,
            p('step_edge_1'),
            p('step_edge_2'),
            p('step_edge_3'),
          ].filter(Boolean)
        : platform === 'firefox'
          ? [p('step_firefox_1'), p('step_firefox_2')]
          : [
              incognito ? p('step_chrome_incognito') : null,
              p('step_chrome_1'),
              p('step_chrome_2'),
              p('step_chrome_3'),
            ].filter(Boolean);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(6px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, rgba(30,41,59,0.98), rgba(51,65,85,0.98))',
            border: '1px solid rgba(96,165,250,0.35)',
            borderRadius: '1.25rem',
            padding: '1.75rem',
            maxWidth: '440px',
            width: '100%',
            color: 'white',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <MonitorDown size={28} color="#60A5FA" />
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>{p('title')}</h3>
          </div>
          <ol style={{ margin: '0 0 1.25rem', paddingLeft: '1.25rem', lineHeight: 1.6, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
            {steps.map((step, i) => (
              <li key={i} style={{ marginBottom: '0.5rem' }}>{step}</li>
            ))}
          </ol>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
              border: 'none',
              color: 'white',
              padding: '0.75rem',
              borderRadius: '10px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {p('understood')}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * PWA install prompt for demo.qawaem.live.
 */
const DemoPwaInstallBanner = ({ compact = false }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`onlineDemo.pwaInstall.${key}`, opts);
  const {
    visible,
    canNativeInstall,
    isIos,
    incognito,
    platform,
    install,
    dismiss,
  } = usePwaInstallPrompt();
  const [showHelp, setShowHelp] = useState(false);

  if (!visible) return null;

  const handleInstallClick = async () => {
    const result = await install();
    if (result?.status === 'accepted') return;
    setShowHelp(true);
  };

  const bannerHint = incognito
    ? p('incognito_hint')
    : canNativeInstall
      ? p('native_install_hint')
      : isIos
        ? p('ios_hint')
        : p('default_hint');

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          marginTop: compact ? '0.75rem' : '1rem',
          marginBottom: compact ? '1rem' : '1.25rem',
          background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.22), rgba(59, 130, 246, 0.12))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(96, 165, 250, 0.4)',
          borderRadius: compact ? '12px' : '14px',
          padding: compact ? '0.85rem 1rem' : '1rem 1.15rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.85rem',
          flexWrap: 'wrap',
          textAlign: 'left',
          boxShadow: '0 6px 24px rgba(37, 99, 235, 0.18)',
        }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
          borderRadius: '10px',
          padding: '0.6rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Smartphone size={compact ? 18 : 20} color="white" />
        </div>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <p style={{
            color: 'white',
            fontWeight: 700,
            fontSize: compact ? '0.875rem' : '0.95rem',
            marginBottom: '0.2rem',
          }}>
            {p('banner_title')}
          </p>
          <p style={{
            color: 'rgba(255,255,255,0.78)',
            fontSize: compact ? '0.75rem' : '0.8rem',
            lineHeight: 1.4,
            margin: 0,
          }}>
            {bannerHint}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center', flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleInstallClick}
            style={{
              background: 'linear-gradient(135deg, #22C55E, #16A34A)',
              border: 'none',
              color: 'white',
              padding: '0.55rem 1rem',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
            }}
          >
            {isIos && !canNativeInstall ? <Share2 size={15} /> : <Download size={15} />}
            {isIos && !canNativeInstall ? p('how_to_install') : p('install')}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={p('close')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '7px',
              width: '1.75rem',
              height: '1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
            }}
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>

      <InstallHelpModal
        open={showHelp}
        onClose={() => setShowHelp(false)}
        platform={platform}
        incognito={incognito}
      />
    </>
  );
};

export default DemoPwaInstallBanner;
